// Phase 24d · Admin tickets — Overview / All / Refunds / Banned / Checkins
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Download, Ban, ShieldOff, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function eur(c) { return `$${(Number(c || 0) / 100).toFixed(2)}`; }

export default function AdminTickets() {
    const [tab, setTab] = useState("overview");
    const [overview, setOverview] = useState(null);
    const [items, setItems] = useState([]);
    const [banned, setBanned] = useState([]);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const ov = await fetch(`${API}/api/admin/tickets/overview`, { credentials: "include" });
            if (ov.ok) setOverview(await ov.json());
            const ls = await fetch(`${API}/api/admin/tickets/list?limit=100`, { credentials: "include" });
            if (ls.ok) setItems((await ls.json()).items || []);
            const bn = await fetch(`${API}/api/admin/tickets/banned-buyers`, { credentials: "include" });
            if (bn.ok) setBanned((await bn.json()).items || []);
        } catch {}
    }, []);

    useEffect(() => { load(); }, [load]);

    const forceRefund = async (id) => {
        const reason = prompt("Reason for force refund?") || "operator_action";
        setBusyId(id);
        try {
            const r = await fetch(`${API}/api/admin/tickets/${id}/force-refund`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason }),
            });
            if (r.ok) { toast.success("Refunded"); load(); }
            else toast.error("Refund failed");
        } catch { toast.error("Network error"); }
        setBusyId(null);
    };

    const voidT = async (id) => {
        const reason = prompt("Reason for void (no refund)?") || "operator_action";
        setBusyId(id);
        try {
            const r = await fetch(`${API}/api/admin/tickets/${id}/void`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason }),
            });
            if (r.ok) { toast.success("Voided"); load(); }
            else toast.error("Void failed");
        } catch { toast.error("Network error"); }
        setBusyId(null);
    };

    const banBuyer = async (id) => {
        const reason = prompt("Reason for ban?") || "fraud";
        setBusyId(id);
        try {
            const r = await fetch(`${API}/api/admin/tickets/${id}/ban-buyer`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason }),
            });
            if (r.ok) { toast.success("Buyer banned"); load(); }
            else toast.error("Ban failed");
        } catch { toast.error("Network error"); }
        setBusyId(null);
    };

    const unban = async (uid) => {
        try {
            const r = await fetch(`${API}/api/admin/tickets/banned-buyers/${uid}/unban`, {
                method: "POST", credentials: "include",
            });
            if (r.ok) { toast.success("Unbanned"); load(); }
        } catch { toast.error("Network error"); }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/admin" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                            <ArrowLeft size={16} /> Admin
                        </Link>
                        <span className="text-neutral-600">·</span>
                        <h1 className="text-base font-semibold flex items-center gap-2">
                            <Ticket size={16} className="text-[#FFFFFF]" /> Tickets
                        </h1>
                        <span className="text-[10px] text-neutral-500 ml-2">Phase 24d · Paid Events</span>
                    </div>
                    <a
                        href={`${API}/api/admin/tickets/audit-log/export.csv`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[#1A1A1A] hover:border-[#FFFFFF]/40"
                        data-testid="admin-tickets-csv"
                    >
                        <Download size={12} /> CSV
                    </a>
                </div>
            </header>
            <main className="max-w-6xl mx-auto px-4 py-5 space-y-4">
                <div className="inline-flex items-center gap-1 rounded-full bg-[#111111] border border-[#1A1A1A] p-0.5" data-testid="admin-tickets-tabs">
                    {["overview", "all", "banned"].map((t) => (
                        <button key={t} onClick={() => setTab(t)}
                            data-testid={`admin-tickets-tab-${t}`}
                            className={`text-xs px-3 py-1.5 rounded-full transition ${tab === t ? "bg-[#1F1F1F] text-white" : "text-[#CCCCCC] hover:text-white"}`}>
                            {t}
                        </button>
                    ))}
                </div>

                {tab === "overview" && overview && (
                    <div data-testid="admin-tickets-overview" className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4">
                                <p className="text-xs text-neutral-500">Revenue (confirmed)</p>
                                <p className="text-2xl font-bold text-[#CCCCCC]" data-testid="admin-tickets-revenue">{eur(overview.revenue_cents)}</p>
                            </div>
                            <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4">
                                <p className="text-xs text-neutral-500">Tickets sold</p>
                                <p className="text-2xl font-bold" data-testid="admin-tickets-sold">{overview.tickets_confirmed}</p>
                            </div>
                            <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4">
                                <p className="text-xs text-neutral-500">Refunds</p>
                                <p className="text-2xl font-bold text-neutral-200">{eur(overview.refunds_cents)}</p>
                            </div>
                            <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4">
                                <p className="text-xs text-neutral-500">Banned buyers</p>
                                <p className="text-2xl font-bold text-[#FAFAFA]">{overview.banned_buyers}</p>
                            </div>
                        </div>
                        <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4">
                            <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Top events by revenue</p>
                            {(overview.top_events || []).length === 0 ? (
                                <p className="text-sm text-neutral-500">No paid tickets yet.</p>
                            ) : (
                                <ul className="text-sm space-y-1">
                                    {overview.top_events.map((e) => (
                                        <li key={e._id} className="flex justify-between border-b border-[#1A1A1A] py-1">
                                            <span className="text-neutral-300 font-mono text-xs">{e._id}</span>
                                            <span className="text-[#CCCCCC]">{eur(e.revenue_cents)} · {e.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {tab === "all" && (
                    <div className="space-y-2" data-testid="admin-tickets-list">
                        {items.length === 0 ? (
                            <p className="text-sm text-neutral-500">No tickets yet.</p>
                        ) : items.map((t) => (
                            <div key={t.ticket_id} className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-mono text-[10px] text-neutral-500 truncate">{t.ticket_id}</p>
                                    <p className="text-xs text-neutral-300 mt-0.5">event={t.event_id.slice(0, 8)}… · buyer={t.buyer_user_id.slice(0, 8)}… · <span className="text-neutral-200">{eur(t.price_paid_cents)}</span> · <span className={t.status === "confirmed" ? "text-[#CCCCCC]" : "text-neutral-400"}>{t.status}</span></p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {t.status === "confirmed" && (
                                        <>
                                            <button onClick={() => forceRefund(t.ticket_id)} disabled={busyId === t.ticket_id}
                                                data-testid={`admin-tickets-refund-${t.ticket_id}`}
                                                className="text-[10px] px-2.5 py-1 rounded-lg border border-neutral-600 text-neutral-200 hover:bg-neutral-800">
                                                Force refund
                                            </button>
                                            <button onClick={() => voidT(t.ticket_id)} disabled={busyId === t.ticket_id}
                                                data-testid={`admin-tickets-void-${t.ticket_id}`}
                                                className="text-[10px] px-2.5 py-1 rounded-lg border border-[#FAFAFA]/40 text-[#FAFAFA] hover:bg-[#FAFAFA]/10">
                                                <ShieldOff size={10} /> Void
                                            </button>
                                            <button onClick={() => banBuyer(t.ticket_id)} disabled={busyId === t.ticket_id}
                                                data-testid={`admin-tickets-ban-${t.ticket_id}`}
                                                className="text-[10px] px-2.5 py-1 rounded-lg border border-[#FAFAFA]/40 text-[#FAFAFA] hover:bg-[#FAFAFA]/10 inline-flex items-center gap-1">
                                                <Ban size={10} /> Ban buyer
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "banned" && (
                    <div className="space-y-2" data-testid="admin-tickets-banned-list">
                        {banned.length === 0 ? (
                            <p className="text-sm text-neutral-500">No banned buyers.</p>
                        ) : banned.map((b) => (
                            <div key={b.user_id} className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-3 flex items-center justify-between">
                                <div>
                                    <p className="font-mono text-[10px] text-neutral-500">{b.user_id}</p>
                                    <p className="text-xs text-neutral-300">{b.reason}</p>
                                </div>
                                <button onClick={() => unban(b.user_id)}
                                    data-testid={`admin-tickets-unban-${b.user_id}`}
                                    className="text-[10px] px-2.5 py-1 rounded-lg border border-[#CCCCCC]/40 text-[#CCCCCC] hover:bg-[#CCCCCC]/10">
                                    Unban
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
